import { createReducer } from "@reduxjs/toolkit";

import { DEFAULT_CONTROLS_PAYLOAD } from "../common/fastshift-table/src/constants";

import * as StateActions from "./actions";
import { FASTSHIFT_LOCALSTORAGE_KEYS, NO_ORG_ID } from "./constants";
import * as StateTypes from "./types";
import { captureErrorWithContext, getUserDomain } from "./utils";

export type FastShiftState = {
    userDetails: StateTypes.UserDetails;
    selectedOrg: StateTypes.Org | null;
    orgs: StateTypes.Orgs;
    orgServers: StateTypes.OrgServers;
    cloudSitesByOrg: StateTypes.AvailableCloudSitesByOrg;
    migrationPlanTotals: StateTypes.MigrationPlanTotals;
    l1Assessments: StateTypes.L1Assessments;
    customerSupportRequests: StateTypes.CustomerSupportRequests;
    instances: StateTypes.InstancesRequest;
    // M2 state
    customerDomains: StateTypes.FastshiftCustomerDomainsState;
    customerDomain: StateTypes.CustomerDomainDetails;
    customerDomainEngagement: StateTypes.CustomerDomainEngagement;
    selectedDomainOrg: StateTypes.CustomerDomainOrg | null;
    supportRequests: StateTypes.SupportRequests;
    pageStateCache: StateTypes.PageStateCache;
};

// States here should be post-mapping, meaning that they are not necessarily the same as the response from the API
// but rather the ideal shape of the data that we want to work with. Mapping can be done in the reducer.
export const initialState: FastShiftState = {
    userDetails: {
        pending: false,
        data: null,
        error: null
    },
    selectedOrg: null,
    selectedDomainOrg: null,
    orgs: {
        pending: false,
        data: [],
        error: null
    },
    orgServers: {
        pending: false,
        data: [],
        error: null
    },
    cloudSitesByOrg: {
        pending: false,
        data: [],
        error: null
    },
    migrationPlanTotals: {
        pending: false,
        data: {
            totalPlansCompleted: 0,
            totalPlansUnsuccessful: 0,
            totalPlansRunning: 0
        },
        error: null
    },
    l1Assessments: {
        pending: false,
        data: [],
        error: null
    },
    customerSupportRequests: {
        pending: false,
        error: null,
        data: []
    },
    instances: {
        pending: false,
        data: { instances: [], totalCount: 0 },
        error: null
    },
    customerDomain: {
        pending: false,
        error: null,
        data: null
    },
    customerDomainEngagement: {
        pending: false,
        error: null,
        data: null
    },

    // states for partner and fastshift team roles only:
    customerDomains: {
        pending: false,
        error: null,
        data: {
            data: [],
            pagination: {
                page: 0,
                size: 20,
                totalElements: 0,
                totalPages: 0,
                hasNext: false,
                hasPrevious: false,
                sorted: false
            }
        }
    },
    supportRequests: {
        pending: false,
        error: null,
        data: []
    },
    pageStateCache: {
        selectionTableControls: DEFAULT_CONTROLS_PAYLOAD,
        migrationStatusTableControls: DEFAULT_CONTROLS_PAYLOAD
    }
};

// collects tickets where user is not an org admin for related site
export const noOrg: StateTypes.Org = {
    id: NO_ORG_ID,
    isAdmin: true,
    name: "No Org" // do not display this name to customers
};

export const reducer = (state: FastShiftState = initialState) =>
    createReducer(state, (builder) => {
        builder
            .addCase(StateActions.setSelectedOrg, (state, { payload }) => {
                state.selectedOrg = payload;
                // save selected org to localstorage
                localStorage.setItem(
                    FASTSHIFT_LOCALSTORAGE_KEYS.SELECTED_ORG_ID,
                    JSON.stringify(payload.id)
                );
            })
            .addCase(
                StateActions.setSelectedDomainOrg,
                (state, { payload }) => {
                    state.selectedDomainOrg = payload;
                }
            )
            .addCase(
                StateActions.getAccountOrgs.fulfilled,
                (state, { payload }) => {
                    state.orgs.pending = false;
                    // filter out orgs where user is not an admin
                    const orgsList =
                        payload?.reduce(
                            (acc: StateTypes.Org[], { id, isAdmin, name }) => {
                                if (isAdmin) {
                                    acc.push({ id, isAdmin, name });
                                }
                                return acc;
                            },
                            []
                        ) ?? [];
                    state.orgs.data = [...orgsList, noOrg];
                    state.orgs.error = null;
                }
            )
            .addCase(StateActions.getAccountOrgs.pending, (state) => {
                state.orgs.pending = true;
                state.orgs.error = null;
            })
            .addCase(StateActions.getAccountOrgs.rejected, (state, action) => {
                state.orgs.pending = false;

                const message = action.error.message ?? "unknown error";
                state.orgs.error = message;

                captureErrorWithContext({
                    error: "Error on fetching account orgs",
                    message,
                    userDomain: getUserDomain(state)
                });
            })
            .addCase(
                StateActions.getOrgServers.fulfilled,
                (state, { payload }) => {
                    state.orgServers.pending = false;
                    state.orgServers.data = payload.data;
                    state.orgServers.error = null;
                }
            )
            .addCase(StateActions.getOrgServers.pending, (state) => {
                state.orgServers.pending = true;
                state.orgServers.error = null;
            })
            .addCase(StateActions.getOrgServers.rejected, (state, action) => {
                state.orgServers.pending = false;
                state.orgServers.data = [];

                const message = action.error.message ?? "unknown error";
                state.orgServers.error = message;

                captureErrorWithContext({
                    error: "Error on fetching org servers",
                    message,
                    userDomain: getUserDomain(state)
                });
            })
            .addCase(StateActions.getAvailableProducts.pending, (state) => {
                state.cloudSitesByOrg.pending = true;
                state.cloudSitesByOrg.error = null;
                state.cloudSitesByOrg.data = [];
            })
            .addCase(
                StateActions.getAvailableProducts.fulfilled,
                (state, { payload }) => {
                    state.cloudSitesByOrg.pending = false;
                    state.cloudSitesByOrg.data = payload;
                    state.cloudSitesByOrg.error = null;
                }
            )
            .addCase(
                StateActions.getAvailableProducts.rejected,
                (state, action) => {
                    state.cloudSitesByOrg.pending = false;
                    state.cloudSitesByOrg.data = [];

                    const message = action.error.message ?? "unknown error";
                    state.cloudSitesByOrg.error = message;

                    captureErrorWithContext({
                        error: "Error on fetching available products",
                        message,
                        userDomain: getUserDomain(state)
                    });
                }
            )
            .addCase(
                StateActions.getMigrationPlanTotals.fulfilled,
                (state, { payload }) => {
                    state.migrationPlanTotals.pending = false;
                    state.migrationPlanTotals.data = payload;
                    state.migrationPlanTotals.error = null;
                }
            )
            .addCase(StateActions.getMigrationPlanTotals.pending, (state) => {
                state.migrationPlanTotals.pending = true;
                state.migrationPlanTotals.error = null;
            })
            .addCase(StateActions.getMigrationPlanTotals.rejected, (state) => {
                state.migrationPlanTotals.pending = false;
                state.migrationPlanTotals.data = {
                    totalPlansCompleted: 0,
                    totalPlansUnsuccessful: 0,
                    totalPlansRunning: 0
                };

                const message = "error fetching migration plan totals";
                state.migrationPlanTotals.error = message;

                captureErrorWithContext({
                    error: "Error on fetching migration plan totals",
                    message,
                    userDomain: getUserDomain(state)
                });
            })
            .addCase(
                StateActions.getL1Assessments.fulfilled,
                (state, { payload }) => {
                    state.l1Assessments.pending = false;
                    state.l1Assessments.data = payload;
                    state.l1Assessments.error = null;
                }
            )
            .addCase(StateActions.getL1Assessments.pending, (state) => {
                state.l1Assessments.pending = true;
                state.l1Assessments.error = null;
            })
            .addCase(
                StateActions.getL1Assessments.rejected,
                (state, action) => {
                    state.l1Assessments.pending = false;
                    state.l1Assessments.data = [];

                    const message = action.error.message ?? "unknown error";
                    state.l1Assessments.error = message;

                    captureErrorWithContext({
                        error: "Error on fetching l1 assessments",
                        message,
                        userDomain: getUserDomain(state)
                    });
                }
            )
            .addCase(StateActions.setResetCustomerSupportRequests, (state) => {
                state.customerSupportRequests.pending = false;
                state.customerSupportRequests.error = null;
                state.customerSupportRequests.data = [];
            })
            .addCase(
                StateActions.getAllCustomerSupportRequests.pending,
                (state) => {
                    state.customerSupportRequests.pending = true;
                    state.customerSupportRequests.error = null;
                }
            )
            .addCase(
                StateActions.getAllCustomerSupportRequests.fulfilled,
                (state, { payload }) => {
                    state.customerSupportRequests.pending = false;
                    state.customerSupportRequests.data = payload.requests;
                    state.customerSupportRequests.error = null;
                }
            )
            .addCase(
                StateActions.getAllCustomerSupportRequests.rejected,
                (state, action) => {
                    state.customerSupportRequests.pending = false;
                    state.customerSupportRequests.data = [];

                    const message = action.error.message ?? "unknown error";
                    state.customerSupportRequests.error = message;

                    captureErrorWithContext({
                        error: "Error on fetching customer support requests",
                        message,
                        userDomain: getUserDomain(state)
                    });
                }
            )
            .addCase(
                StateActions.getInstances.fulfilled,
                (state, { payload }) => {
                    state.instances.pending = false;
                    state.instances.data.instances = payload.instances;
                    state.instances.data.totalCount = payload.totalCount;
                    state.instances.error = null;
                }
            )
            .addCase(StateActions.getInstances.pending, (state) => {
                state.instances.pending = true;
                state.instances.error = null;
            })
            .addCase(StateActions.getInstances.rejected, (state, action) => {
                state.instances.pending = false;
                state.instances.data = { instances: [], totalCount: 0 };

                const message = action.error.message ?? "unknown error";
                state.instances.error = message;

                captureErrorWithContext({
                    error: "Error on fetching MMA instances",
                    message,
                    userDomain: getUserDomain(state)
                });
            })
            .addCase(
                StateActions.getCustomerDomains.fulfilled,
                (state, { payload }) => {
                    state.customerDomains.pending = false;
                    state.customerDomains.data = payload;
                    state.customerDomains.error = null;
                }
            )
            .addCase(StateActions.getCustomerDomains.pending, (state) => {
                state.customerDomains.pending = true;
                state.customerDomains.error = null;
            })
            .addCase(
                StateActions.getCustomerDomains.rejected,
                (state, action) => {
                    state.customerDomains.pending = false;
                    state.customerDomains.data = {
                        data: [],
                        pagination: {
                            page: 0,
                            size: 20,
                            totalElements: 0,
                            totalPages: 0,
                            hasNext: false,
                            hasPrevious: false,
                            sorted: false
                        }
                    };

                    const message = action.error.message ?? "unknown error";
                    state.customerDomains.error = message;

                    captureErrorWithContext({
                        error: "Error on fetching customer domains list",
                        message,
                        userDomain: getUserDomain(state)
                    });
                }
            )
            .addCase(
                StateActions.getCustomerDomain.fulfilled,
                (state, { payload }) => {
                    state.customerDomain.pending = false;
                    state.customerDomain.data = payload;
                    state.customerDomain.error = null;
                }
            )
            .addCase(StateActions.getCustomerDomain.pending, (state) => {
                state.customerDomain.pending = true;
                state.customerDomain.error = null;
            })
            .addCase(
                StateActions.getCustomerDomain.rejected,
                (state, action) => {
                    state.customerDomain.pending = false;
                    state.customerDomain.data = null;
                    const message = action.error.message ?? "unknown error";
                    state.customerDomain.error = message;

                    captureErrorWithContext({
                        error: "Error on fetching customer domain details",
                        message,
                        userDomain: getUserDomain(state)
                    });
                }
            )
            .addCase(
                StateActions.getCustomerDomainEngagementDetails.fulfilled,
                (state, { payload }) => {
                    state.customerDomainEngagement.pending = false;
                    state.customerDomainEngagement.data = payload;
                    state.customerDomainEngagement.error = null;
                }
            )
            .addCase(
                StateActions.getCustomerDomainEngagementDetails.pending,
                (state) => {
                    state.customerDomainEngagement.pending = true;
                    state.customerDomainEngagement.error = null;
                }
            )
            .addCase(
                StateActions.getCustomerDomainEngagementDetails.rejected,
                (state, action) => {
                    state.customerDomainEngagement.pending = false;
                    state.customerDomainEngagement.data = null;
                    const message = action.error.message ?? "unknown error";
                    state.customerDomainEngagement.error = message;

                    // check if error is expected
                    const isExpectedError = Object.values(
                        StateTypes.CustomerDomainEngagementExpectedError
                    ).includes(
                        message as StateTypes.CustomerDomainEngagementExpectedError
                    );

                    // if unexpected, capture on Sentry
                    if (!isExpectedError) {
                        captureErrorWithContext({
                            error: "Error on fetching customer domain engagement details",
                            message,
                            userDomain: getUserDomain(state)
                        });
                    }
                }
            )
            // Fastshift support requests (M2-specific)
            .addCase(
                StateActions.getFastshiftSupportRequests.pending,
                (state) => {
                    state.supportRequests.pending = true;
                    state.supportRequests.error = null;
                    state.supportRequests.data = [];
                }
            )
            .addCase(
                StateActions.getFastshiftSupportRequests.fulfilled,
                (state, { payload }) => {
                    state.supportRequests.pending = false;
                    state.supportRequests.data = payload.data;
                    state.supportRequests.error = null;
                }
            )
            .addCase(
                StateActions.getFastshiftSupportRequests.rejected,
                (state, action) => {
                    if (
                        action.error.name === StateTypes.FastshiftApiAbortError
                    ) {
                        return;
                    }

                    state.supportRequests.pending = false;
                    state.supportRequests.data = [];
                    const message = action.error.message ?? "unknown error";

                    state.supportRequests.error = message;

                    captureErrorWithContext({
                        error: "Error on fetching support requests",
                        message,
                        userDomain: getUserDomain(state)
                    });
                }
            )
            .addCase(
                StateActions.clearAllCustomerDomainScopedState,
                (state) => {
                    state.customerDomain.pending = false;
                    state.customerDomain.error = null;
                    state.customerDomain.data = null;
                    state.customerDomainEngagement.pending = false;
                    state.customerDomainEngagement.error = null;
                    state.customerDomainEngagement.data = null;
                    state.selectedDomainOrg = null;
                    state.pageStateCache.migrationStatusTableControls =
                        DEFAULT_CONTROLS_PAYLOAD;
                }
            )
            .addCase(StateActions.clearCustomerDomain, (state) => {
                state.customerDomain.pending = false;
                state.customerDomain.data = null;
                state.customerDomain.error = null;
            })
            .addCase(
                StateActions.setSelectionTableControls,
                (state, { payload }) => {
                    state.pageStateCache.selectionTableControls =
                        payload.selectionTableControls;
                }
            )
            .addCase(
                StateActions.setMigrationStatusTableControls,
                (state, { payload }) => {
                    state.pageStateCache.migrationStatusTableControls =
                        payload.migrationStatusTableControls;
                }
            )
            .addCase(
                StateActions.getUserDetails.fulfilled,
                (state, { payload }) => {
                    state.userDetails.pending = false;
                    state.userDetails.data = payload;
                    state.userDetails.error = null;
                }
            )
            .addCase(StateActions.getUserDetails.pending, (state) => {
                state.userDetails.pending = true;
                state.userDetails.data = null;
                state.userDetails.error = null;
            })
            .addCase(StateActions.getUserDetails.rejected, (state, action) => {
                state.userDetails.pending = false;
                state.userDetails.data = null;

                const message = action.error.message ?? "unknown error";
                state.userDetails.error = message;

                // check if error is expected
                const isExpectedError = Object.values(
                    StateTypes.FastshiftUserDetailsExpectedError
                ).includes(
                    message as StateTypes.FastshiftUserDetailsExpectedError
                );

                // if unexpected, capture on Sentry
                if (!isExpectedError) {
                    captureErrorWithContext({
                        error: "Error on fetching user details",
                        message
                    });
                }
            });
    });
